import React from 'react';
import { FormField, FormStep, Option } from '../types';
import FilePreview from '../components/FilePreview';

export default function ReviewView({ groups, values, resolvedOptions }: {
  groups: { step: FormStep; fields: FormField[] }[];
  values: Record<string, unknown>;
  resolvedOptions: Record<string, Option[]>;
}) {
  const reviewValue = (field: FormField) => {
    const value = values[field.key];
    if (field.type === 'file') {
      const files = Array.isArray(value) ? value.filter((v): v is File => v instanceof File) : value instanceof File ? [value] : [];
      return files.length ? <FilePreview files={files} /> : <span className="text-slate-400">No file</span>;
    }
    if (value == null || value === '') return '—';
    if (['multi_select', 'checkbox_group'].includes(field.type) && Array.isArray(value)) {
      const options = resolvedOptions[field.key] || field.options;
      return value.map((v) => options.find((o) => String(o.value) === String(v))?.label || String(v)).join(', ') || '—';
    }
    if (Array.isArray(value)) return value.map(String).join(', ') || '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (['select', 'radio'].includes(field.type)) {
      const option = (resolvedOptions[field.key] || field.options).find((o) => String(o.value) === String(value));
      return option?.label || String(value);
    }
    return String(value);
  };
  return (
        <div className="space-y-3">
          {groups.map(({ step: reviewStep, fields: stepFields }, stepIndex) => (
            <details key={reviewStep.id} open className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-slate-50 px-3 py-2.5 text-[11px] font-semibold text-slate-700 [&::-webkit-details-marker]:hidden">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-slate-200 text-[9px] text-slate-600">{stepIndex + 1}</span>
                  <span className="truncate">{reviewStep.title}</span>
                </span>
                <span className="shrink-0 text-[9px] font-medium text-slate-400">{stepFields.length} {stepFields.length === 1 ? 'field' : 'fields'}</span>
              </summary>
              <div className="space-y-2.5 border-t border-slate-100 p-2.5">
                {stepFields.map((f) => (
                  <div key={f.key} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{f.label}</div>
                    <div className="mt-1.5 text-[12px] leading-5 text-slate-700">{reviewValue(f)}</div>
                  </div>
                ))}
              </div>
            </details>
          ))}
          {groups.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-[11px] text-slate-400">No fields are marked to show in review.</div>}
        </div>
  );
}
